import * as React from 'react';
import {
    Panel, PanelType, Stack, Text, ComboBox, IComboBoxOption, IComboBox,
    PrimaryButton, DefaultButton, MessageBar, MessageBarType,
    Spinner, SpinnerSize, mergeStyles, Separator, IconButton, ActionButton
} from '@fluentui/react';
import { LanguageContext } from '../../utilities/LocalizationModule';
import { getOrphanCustomers, adoptOrphanCustomer, getAllTenantsLite } from '../../utilities/helpers/ApiHelper';

interface IOrphanSubscriptionItem {
    productId: string;
    description: string;
    amount: number;
    quantity: number;
}

interface IOrphanSubscription {
    id: string;
    status: string;
    currency: string;
    totalAmount: number;
    items: IOrphanSubscriptionItem[];
}

interface IOrphanEvent {
    id: number;
    stripeEventId: string;
    eventType: string;
    objectId: string;
    receivedAt: string;
    lastError: string;
}

export interface IOrphanCustomer {
    customerId: string;
    customerEmail: string;
    customerName: string;
    customerCountry: string;
    hasTenantGuidOnCustomer: boolean;
    deletedCustomer: boolean;
    events: IOrphanEvent[];
    subscriptions: IOrphanSubscription[];
}

interface IOrphansResponse {
    orphans: IOrphanCustomer[];
    truncated: boolean;
    unresolvedEvents: IOrphanEvent[];
}

interface ITenantLite {
    id: number;
    tenantId: string;
    friendlyName: string;
}

interface Props {
    isOpen: boolean;
    onDismiss: () => void;
    // Called after every successful adoption so the parent (Subscriptions view) can refresh
    // its toolbar badge / banner without waiting for the panel to close.
    onCountChanged?: () => void;
}

const sectionLabel = mergeStyles({
    fontWeight: 600,
    marginTop: 8,
    marginBottom: 4,
});

const monoStyle = mergeStyles({
    fontFamily: 'Consolas, monospace',
    fontSize: 12,
    color: "var(--db-fg)",
});

const rowCardStyle = mergeStyles({
    border: `1px solid var(--db-border)`,
    borderRadius: 4,
    marginBottom: 8,
    backgroundColor: "var(--db-surface-raised)",
});

const rowHeaderStyle = mergeStyles({
    padding: '10px 12px',
    cursor: 'pointer',
    selectors: {
        ':hover': { backgroundColor: "var(--db-hover-bg)" },
    },
});

const rowBodyStyle = mergeStyles({
    padding: '0 12px 12px 12px',
    borderTop: `1px solid var(--db-border)`,
});

function formatAmount(minor: number, currency: string): string {
    if (!minor && minor !== 0) return '—';
    const major = minor / 100;
    try {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: (currency || 'EUR').toUpperCase() }).format(major);
    } catch {
        return `${(currency || '').toUpperCase()} ${major.toFixed(2)}`;
    }
}

function shortGuid(g: string): string {
    if (!g) return '';
    return g.length > 8 ? g.substring(0, 8) : g;
}

// ─── Row sub-component ────────────────────────────────────────────────────────

interface RowProps {
    orphan: IOrphanCustomer;
    tenantOptions: IComboBoxOption[];
    tenantsLoading: boolean;
    onAdopted: (customerId: string) => void;
}

const OrphanRow: React.FC<RowProps> = ({ orphan, tenantOptions, tenantsLoading, onAdopted }) => {
    const languageStrings = React.useContext(LanguageContext);
    const [expanded, setExpanded] = React.useState<boolean>(false);
    const [selectedTenantId, setSelectedTenantId] = React.useState<number | null>(null);
    const [isAdopting, setIsAdopting] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null>(null);

    const handleAdopt = async () => {
        if (!selectedTenantId) return;
        setIsAdopting(true);
        setError(null);
        try {
            const result = await adoptOrphanCustomer(orphan.customerId, selectedTenantId);
            if (result && result.customerId) {
                onAdopted(orphan.customerId);
            } else {
                setError('Adoption failed. The customer or one of its subscriptions could not be updated in Stripe.');
            }
        } catch (e) {
            setError((e as Error)?.message || 'Adoption failed.');
        } finally {
            setIsAdopting(false);
        }
    };

    const totals = orphan.subscriptions.reduce((acc, s) => {
        const cur = (s.currency || 'eur').toLowerCase();
        acc[cur] = (acc[cur] || 0) + (s.totalAmount || 0);
        return acc;
    }, {} as Record<string, number>);
    const totalText = Object.keys(totals).length === 0
        ? '—'
        : Object.keys(totals).map(c => formatAmount(totals[c], c)).join(' / ');

    return (
        <div className={rowCardStyle}>
            <div
                className={rowHeaderStyle}
                onClick={() => setExpanded(!expanded)}
                role="button"
                tabIndex={0}
                onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); setExpanded(!expanded); } }}
            >
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                    <IconButton
                        iconProps={{ iconName: expanded ? 'ChevronDown' : 'ChevronRight' }}
                        ariaLabel={expanded ? 'Collapse' : 'Expand'}
                        styles={{ root: { height: 24, width: 24 } }}
                    />
                    <Stack grow>
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                            <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
                                {orphan.customerEmail || '—'}
                            </Text>
                            {orphan.customerCountry ? (
                                <Text variant="small" styles={{ root: { color: "var(--db-fg-muted)" } }}>
                                    · {orphan.customerCountry}
                                </Text>
                            ) : null}
                            {orphan.customerName ? (
                                <Text variant="small" styles={{ root: { color: "var(--db-fg-muted)" } }}>
                                    · {orphan.customerName}
                                </Text>
                            ) : null}
                            {orphan.deletedCustomer ? (
                                <Text variant="small" styles={{ root: { color: "var(--db-error-fg)", fontWeight: 600 } }}>
                                    · {languageStrings.CustomerDeleted}
                                </Text>
                            ) : null}
                            {orphan.hasTenantGuidOnCustomer ? (
                                <Text variant="small" styles={{ root: { color: "var(--db-success-fg)", fontWeight: 600 } }}>
                                    · {languageStrings.HasTenantGuidStamped}
                                </Text>
                            ) : null}
                        </Stack>
                        <Text variant="small" className={monoStyle}>
                            {orphan.customerId} · {orphan.events.length} {languageStrings.StuckEvents} · {orphan.subscriptions.length} {languageStrings.StuckSubscriptions} · {totalText}
                        </Text>
                    </Stack>
                </Stack>
            </div>

            {expanded ? (
                <div className={rowBodyStyle}>
                    <Text className={sectionLabel}>{languageStrings.StuckSubscriptions}</Text>
                    {orphan.subscriptions.length === 0 ? (
                        <Text styles={{ root: { color: "var(--db-fg-muted)" } }}>—</Text>
                    ) : (
                        orphan.subscriptions.map(sub => (
                            <Stack key={sub.id} tokens={{ childrenGap: 2 }} styles={{ root: { marginBottom: 8, paddingLeft: 8, borderLeft: `2px solid var(--db-border)` } }}>
                                <Text className={monoStyle}>{sub.id} · {sub.status} · {formatAmount(sub.totalAmount, sub.currency)}</Text>
                                {sub.items.map((it, idx) => (
                                    <Text key={idx} variant="small" styles={{ root: { color: "var(--db-fg-muted)" } }}>
                                        {it.quantity}× {it.description || it.productId || '(item)'} — {formatAmount(it.amount, sub.currency)}
                                    </Text>
                                ))}
                            </Stack>
                        ))
                    )}

                    <Text className={sectionLabel}>{languageStrings.StuckEvents}</Text>
                    <Stack tokens={{ childrenGap: 2 }}>
                        {orphan.events.map(ev => (
                            <Text key={ev.id} variant="small" className={monoStyle}>
                                {ev.eventType} · {new Date(ev.receivedAt).toLocaleString()}
                            </Text>
                        ))}
                    </Stack>

                    <Separator />

                    <Text className={sectionLabel}>{languageStrings.PickTenant}</Text>
                    {tenantsLoading ? (
                        <Spinner size={SpinnerSize.small} label={languageStrings.Loading} />
                    ) : (
                        <ComboBox
                            placeholder={languageStrings.PickTenantPlaceholder}
                            options={tenantOptions}
                            selectedKey={selectedTenantId ?? undefined}
                            allowFreeform={false}
                            autoComplete="on"
                            useComboBoxAsMenuWidth
                            onChange={(_: React.FormEvent<IComboBox>, option?: IComboBoxOption) => {
                                if (option) setSelectedTenantId(option.key as number);
                            }}
                            disabled={isAdopting || orphan.deletedCustomer}
                        />
                    )}

                    <Stack horizontal tokens={{ childrenGap: 8 }} styles={{ root: { marginTop: 12 } }}>
                        <PrimaryButton
                            text={isAdopting ? '' : languageStrings.AdoptAndReprocess}
                            disabled={!selectedTenantId || isAdopting || orphan.deletedCustomer}
                            onClick={handleAdopt}
                            onRenderIcon={isAdopting ? () => <Spinner size={SpinnerSize.small} /> : undefined}
                        />
                    </Stack>

                    <MessageBar messageBarType={MessageBarType.warning} isMultiline={true} styles={{ root: { marginTop: 8 } }}>
                        {languageStrings.AdoptOrphanWarning}
                    </MessageBar>

                    {error ? (
                        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)} isMultiline={true} styles={{ root: { marginTop: 8 } }}>
                            {error}
                        </MessageBar>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};

// ─── Panel ────────────────────────────────────────────────────────────────────

const OrphanCustomersPanel: React.FC<Props> = ({ isOpen, onDismiss, onCountChanged }) => {
    const languageStrings = React.useContext(LanguageContext);
    const [data, setData] = React.useState<IOrphansResponse | null>(null);
    const [tenants, setTenants] = React.useState<ITenantLite[]>([]);
    const [tenantsLoading, setTenantsLoading] = React.useState<boolean>(false);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null>(null);
    const [successMessage, setSuccessMessage] = React.useState<string | null>(null);

    const fetchOrphans = React.useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const result: IOrphansResponse = await getOrphanCustomers();
            if (result) {
                setData(result);
            } else {
                setError('Failed to load orphan customers.');
            }
        } catch (e) {
            setError((e as Error)?.message || 'Failed to load orphan customers.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    React.useEffect(() => {
        if (!isOpen) {
            setSuccessMessage(null);
            setError(null);
            return;
        }
        fetchOrphans();
        // Tenants don't change between adoptions; load once per panel-open.
        let cancelled = false;
        (async () => {
            setTenantsLoading(true);
            try {
                const result: ITenantLite[] = await getAllTenantsLite();
                if (!cancelled && Array.isArray(result)) {
                    setTenants(result);
                }
            } finally {
                if (!cancelled) setTenantsLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [isOpen, fetchOrphans]);

    const tenantOptions: IComboBoxOption[] = React.useMemo(() => {
        return tenants.map(t => ({
            key: t.id,
            text: `${t.friendlyName || '(unnamed)'}  ·  ${shortGuid(t.tenantId)}`,
            data: t,
        }));
    }, [tenants]);

    const handleAdopted = (_customerId: string) => {
        setSuccessMessage(languageStrings.OrphanAdopted);
        if (onCountChanged) onCountChanged();
        // Refresh the list so the adopted row disappears.
        fetchOrphans();
        window.setTimeout(() => setSuccessMessage(null), 6000);
    };

    return (
        <Panel
            isOpen={isOpen}
            onDismiss={onDismiss}
            headerText={`${languageStrings.OrphanCustomers}${data ? ` (${data.orphans.length})` : ''}`}
            type={PanelType.custom}
            customWidth="640px"
            isBlocking
            closeButtonAriaLabel={languageStrings.Cancel}
        >
            <Stack tokens={{ childrenGap: 8 }}>
                <Text variant="small" styles={{ root: { color: "var(--db-fg-muted)" } }}>
                    {languageStrings.OrphanCustomersDescription}
                </Text>

                <Stack horizontal horizontalAlign="end">
                    <ActionButton
                        iconProps={{ iconName: 'Refresh' }}
                        text={languageStrings.Refresh}
                        onClick={fetchOrphans}
                        disabled={isLoading}
                    />
                </Stack>

                {successMessage ? (
                    <MessageBar messageBarType={MessageBarType.success} onDismiss={() => setSuccessMessage(null)} isMultiline={false}>
                        {successMessage}
                    </MessageBar>
                ) : null}

                {error ? (
                    <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)} isMultiline={true}>
                        {error}
                    </MessageBar>
                ) : null}

                {data?.truncated ? (
                    <MessageBar messageBarType={MessageBarType.warning} isMultiline={false}>
                        {'More than 100 orphan customers found — only the first 100 are shown.'}
                    </MessageBar>
                ) : null}

                {isLoading ? (
                    <Stack horizontalAlign="center" tokens={{ padding: 30 }}>
                        <Spinner size={SpinnerSize.large} label={languageStrings.Loading} />
                    </Stack>
                ) : (
                    <>
                        {data && data.orphans.length === 0 ? (
                            <Stack horizontalAlign="center" tokens={{ padding: 30 }}>
                                <Text styles={{ root: { color: "var(--db-success-fg)", fontWeight: 600 } }}>
                                    {languageStrings.NoOrphans}
                                </Text>
                            </Stack>
                        ) : (
                            <Stack>
                                {(data?.orphans || []).map(o => (
                                    <OrphanRow
                                        key={o.customerId}
                                        orphan={o}
                                        tenantOptions={tenantOptions}
                                        tenantsLoading={tenantsLoading}
                                        onAdopted={handleAdopted}
                                    />
                                ))}
                            </Stack>
                        )}

                        {data && data.unresolvedEvents && data.unresolvedEvents.length > 0 ? (
                            <>
                                <Separator />
                                <Text variant="small" styles={{ root: { color: "var(--db-fg-muted)" } }}>
                                    {languageStrings.UnresolvedDeadLetteredEvents} ({data.unresolvedEvents.length})
                                </Text>
                            </>
                        ) : null}
                    </>
                )}
            </Stack>
        </Panel>
    );
};

export default OrphanCustomersPanel;
